import React, { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import type { CreateFlightItemViewModel } from './models'
import { createFlightPayloadFromViewModel } from './utils'
import { formOptions } from '../Common/Fields/config'
import FlightFields from '../Common/FlightFields'

import {
  addToTripSaveErrorEvent,
  addToTripSaveEvent,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { useCreateTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import type { CreateTripItemRankSpec } from 'tripPlanner/api/tripItem/types'
import { scrollToNewItemWithRetry } from 'tripPlanner/utils/scrollToNewItem'

interface Props {
  tripId: string
  defaultDate?: string
  initialData?: Partial<CreateFlightItemViewModel>
  rankSpec?: CreateTripItemRankSpec
}

function CreateFlight(props: Props) {
  const { tripId, defaultDate, initialData, rankSpec } = props
  const intl = useIntl()

  const { closeModal, goBack } = useModalCallbacks()
  const methods = useForm<CreateFlightItemViewModel>({
    ...formOptions,
    defaultValues: {
      type: 'FLIGHT',
      sourceType: 'NA',
      startDate: initialData?.startDate ?? defaultDate,
      endDate: initialData?.endDate ?? defaultDate,
      startPlace: {
        ...initialData?.startPlace,
      },
      endPlace: {
        ...initialData?.endPlace,
      },
      flightNumber: initialData?.flightNumber,
      airlineName: initialData?.airlineName,
      destinationAirportCode: initialData?.destinationAirportCode,
      originAirportCode: initialData?.originAirportCode,
      isEndDateSame: initialData?.isEndDateSame,
      confirmationCode: initialData?.confirmationCode,
      isBooked: !!initialData?.confirmationCode,
    },
  })
  const { handleSubmit } = methods

  const onInvalid = useCallback(() => {
    fireInteractionEvent(addToTripSaveErrorEvent('flights_custom_manual'))
  }, [])

  const { mutate: createMutate, isLoading } = useCreateTripItem({
    onSuccess: (res) => {
      fireInteractionEvent(addToTripSaveEvent('flights_custom_manual'))
      scrollToNewItemWithRetry(res.id)
      closeModal()
    },
    onError: onInvalid,
  })

  const createFlightItem = useCallback(
    (viewModel: CreateFlightItemViewModel) => {
      createMutate({
        tripId,
        tripItem: createFlightPayloadFromViewModel(viewModel),
        rankSpec,
      })
    },
    [createMutate, tripId, rankSpec],
  )

  const modalTitle = intl.formatMessage({
    id: 'D9g50WGucg',
    defaultMessage: 'Add flight manually',
    description: 'Modal title when manually adding a flight to trip',
  })
  const saveLabel = intl.formatMessage({
    id: '4oajS+46fz',
    defaultMessage: 'Save',
    description: 'Save button label in the add flight modal',
  })

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <FormProvider {...methods}>
        <ModalBody>
          <ModalContent>
            <form noValidate>
              <FlightFields tripId={tripId} />
            </form>
          </ModalContent>
        </ModalBody>

        <ModalFooter
          primaryActionProps={{
            onClick: handleSubmit(createFlightItem, onInvalid),
            children: saveLabel,
            disabled: isLoading,
          }}
        />
      </FormProvider>

      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default CreateFlight
